import { useMemo, type ReactElement } from 'react'
import { createParser, useQueryState } from 'nuqs'
import { parseAsType } from '@util'
import { getName, isCompanyId, isNotEmpty, isTeamId, isUserId, type DispositionScopeId } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Select, Switch } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import {
  getLevelName,
  translateLabels,
  useDispositions,
  useMultiAccountFeature,
  useProfile,
  useTeam,
  useTranslation,
} from '@front/model'
import { ButtonAdd, NotReady, Page, TAlert, WorkflowDeprecatedCheck, WorkflowDisabledTooltip } from '@components'
import { ScopeSelect } from './ScopeSelect'
import { DispositionList } from './DispositionList'

const LIST_OPTIONS = [
  { value: 'parent', label: 'For Business Accounts' },
  { value: 'child', label: 'For Employee Accounts' },
]
function DispositionsPage(): ReactElement {
  return (
    <WorkflowDeprecatedCheck>
      {({ isWorkflowForCanvassEnabled }) => (
        <Dispositions isWorkflowEnabled={isWorkflowForCanvassEnabled} />
      )}
    </WorkflowDeprecatedCheck>
  )
}

const Dispositions = ({ isWorkflowEnabled }: { isWorkflowEnabled: boolean }): ReactElement => {
  const { t } = useTranslation()
  const isUsingMultiAccountArch = useMultiAccountFeature().isEnabled

  const [showArchived, { toggle }] = useQueryDisclosure('showArchived')
  const [listType, setListType] = useQueryState<ListType>('listType', parseAsListType.withDefault('child'))
  const [scopeId, setScopeId] = useQueryState<DispositionScopeId>('scopeId', parseAsDispositionScopeId)
  const isParentList = useMemo(
    () => isUsingMultiAccountArch && listType === 'parent',
    [isUsingMultiAccountArch, listType]
  )
  const listInput = useMemo(() => {
    if (isParentList) {
      return { parentOnly: true }
    }
    return { parentOnly: false }
  }, [isParentList])

  const { data, isLoading, error, isFetching, refetch } = useDispositions({
    archived: showArchived ? 'all' : false,
    scopeId: scopeId ?? undefined,
    ...listInput,
  })

  const { data: teamData } = useTeam(isTeamId(scopeId) ? scopeId : undefined)
  const { company, user } = useProfile()

  const scopeName = useMemo(() => {
    if (!scopeId) return
    if (isUserId(scopeId)) return getName(user)
    if (isCompanyId(scopeId)) return company?.name
    if (!teamData) return scopeId
    const { team } = teamData
    return team?.level ? `${getLevelName(company, team?.level)}: ${team?.name}` : team?.name
  }, [scopeId, teamData, user, company])

  const listOptions = useMemo(() => translateLabels(LIST_OPTIONS, t), [t])

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Dispositions' isLoading={isLoading} />

  const dispositionsExist = isNotEmpty(data)

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Dispositions')}>
        {isUsingMultiAccountArch && (
          <Select
            allowDeselect={false}
            data={listOptions}
            value={listType}
            onChange={(value) => {
              if (!value || !isListType(value)) return
              setListType(value)
            }}
          />
        )}
        <WorkflowDisabledTooltip isWorkflowEnabled={isWorkflowEnabled}>
          <ButtonAdd component={Link} to={isParentList ? 'add?isParent=true' : 'add'} />
        </WorkflowDisabledTooltip>
      </Page.Heading>
      <Page.FunctionBar justify='end'>
        <Switch checked={showArchived} label={t('Show Archived')} labelPosition='left' onChange={toggle} />
        {!isParentList && (
          <ScopeSelect
            allowDeselect
            emptyLabel={t('All Your Dispositions')}
            scopeId={scopeId ?? undefined}
            onChange={(scopeId) => setScopeId(scopeId ?? null)}
          />
        )}
      </Page.FunctionBar>
      <Page.Content scrollArea>
        {dispositionsExist ? (
          <DispositionList canReorder={!isWorkflowEnabled} dispositions={data} />
        ) : (
          <TAlert title={t('No Dispositions Found for {{scope}}', { scope: scopeName })} />
        )}
      </Page.Content>
    </Page>
  )
}

const parseAsDispositionScopeId = createParser({
  parse(queryValue) {
    const decoded = decodeURIComponent(queryValue)
    if (isUserId(decoded)) {
      return decoded
    }
    if (isTeamId(decoded)) {
      return decoded
    }
    if (isCompanyId(decoded)) {
      return decoded
    }
    return null
  },
  serialize(value) {
    return encodeURIComponent(value)
  },
})

type ListType = 'parent' | 'child'

const isListType = (value: string): value is ListType => value === 'parent' || value === 'child'

const parseAsListType = parseAsType(isListType)

export const Route = createFileRoute('/settings/disposition/')({
  component: DispositionsPage,
})
